package diana.soleil.design.iraniancanadienbazzar.core.data

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.model.Fact
import diana.soleil.design.iraniancanadienbazzar.core.model.Joke
import diana.soleil.design.iraniancanadienbazzar.core.model.Poem
import diana.soleil.design.iraniancanadienbazzar.core.model.Poet
import diana.soleil.design.iraniancanadienbazzar.core.model.Recipe
import diana.soleil.design.iraniancanadienbazzar.core.model.SavedGem
import diana.soleil.design.iraniancanadienbazzar.core.model.Story
import diana.soleil.design.iraniancanadienbazzar.core.model.Tale
import kotlinx.coroutines.flow.Flow

/**
 * Hidden Gems content + per-user state (likes, saved bookmarks, 18+ opt-in). Content is read-only
 * from the app (admin/seed-published `jokes`/`recipes` collections — see `docs/DATA_MODEL.md`
 * § Hidden Gems); like counts are Function-maintained. Search/filtering happens client-side over
 * the streamed corpus (SearchViewModel pattern — the corpus is small by design).
 */
interface GemsRepository {

    /** The signed-in (non-anonymous) uid, or null — gates like/save affordances. */
    val currentUid: String?

    /**
     * Streams published jokes newest-first. When [includeAdult] is false the query filters
     * `maturity == 'clean'` server-side — mandatory for signed-out sessions (rules deny adult
     * reads to anonymous users) and the default everywhere until the 18+ opt-in is granted.
     */
    fun observeJokes(includeAdult: Boolean, limit: Int = 200): Flow<DataResult<List<Joke>>>

    /** Streams published recipes newest-first. */
    fun observeRecipes(limit: Int = 200): Flow<DataResult<List<Recipe>>>

    /** Streams published Danestaniha facts newest-first (`facts` collection). */
    fun observeFacts(limit: Int = 200): Flow<DataResult<List<Fact>>>

    /** Streams published Shahnameh tales newest-first (`tales` collection). */
    fun observeTales(limit: Int = 200): Flow<DataResult<List<Tale>>>

    /** One-shot read for the tale detail screen (deep links land here without the list). */
    suspend fun getTale(taleId: String): DataResult<Tale>

    /** Streams the poets of the Persian poetry section in hub order (`poets` collection). */
    fun observePoets(limit: Int = 100): Flow<DataResult<List<Poet>>>

    /**
     * Streams published poems newest-first — one poet's when [poetId] is given, the whole corpus
     * when null (the hub resolves saved poems against it).
     */
    fun observePoems(poetId: String? = null, limit: Int = 200): Flow<DataResult<List<Poem>>>

    /** One-shot read for the poem detail screen (deep links land here without the list). */
    suspend fun getPoem(poemId: String): DataResult<Poem>

    /** Streams published short stories newest-first (`stories` collection). */
    fun observeStories(limit: Int = 200): Flow<DataResult<List<Story>>>

    /** One-shot read for the story detail screen (deep links land here without the list). */
    suspend fun getStory(storyId: String): DataResult<Story>

    /** Ids of poems the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedPoemIds(): Flow<Set<String>>

    /** Ids of stories the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedStoryIds(): Flow<Set<String>>

    /** Adds/removes the caller's like on a poem; guests get Unauthorized. */
    suspend fun setPoemLiked(poemId: String, liked: Boolean): DataResult<Unit>

    /** Adds/removes the caller's like on a story; guests get Unauthorized. */
    suspend fun setStoryLiked(storyId: String, liked: Boolean): DataResult<Unit>

    /** One-shot read for the detail/cook screens (deep links land here without the list). */
    suspend fun getRecipe(recipeId: String): DataResult<Recipe>

    /** Ids of jokes the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedJokeIds(): Flow<Set<String>>

    /** Ids of recipes the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedRecipeIds(): Flow<Set<String>>

    /** Ids of facts the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedFactIds(): Flow<Set<String>>

    /** Ids of tales the signed-in user liked (collection-group stream; empty for guests). */
    fun observeLikedTaleIds(): Flow<Set<String>>

    /** Adds/removes the caller's like on a joke; guests get [diana.soleil.design.iraniancanadienbazzar.core.common.AppError.Unauthorized]. */
    suspend fun setJokeLiked(jokeId: String, liked: Boolean): DataResult<Unit>

    /** Adds/removes the caller's like on a recipe; guests get Unauthorized. */
    suspend fun setRecipeLiked(recipeId: String, liked: Boolean): DataResult<Unit>

    /** Adds/removes the caller's like on a fact; guests get Unauthorized. */
    suspend fun setFactLiked(factId: String, liked: Boolean): DataResult<Unit>

    /** Adds/removes the caller's like on a tale; guests get Unauthorized. */
    suspend fun setTaleLiked(taleId: String, liked: Boolean): DataResult<Unit>

    /**
     * Streams the signed-in user's cloud bookmarks (`users/{uid}/savedGems`), or `null` while
     * signed out — the null lets [GemsSavedController] distinguish "guest, local-only" from
     * "signed in with zero saves".
     */
    fun observeSavedGemsCloud(): Flow<Set<SavedGem>?>

    /** Writes/deletes one cloud bookmark. No-op failure for guests (local prefs still hold it). */
    suspend fun setSavedGemCloud(gem: SavedGem, saved: Boolean): DataResult<Unit>

    /**
     * Streams the account's 18+ opt-in (`users/{uid}.adultContentOptIn`); `null` while signed out
     * or before the doc exists. Guests keep a device-local pref instead (see JokesViewModel).
     */
    fun observeAdultOptIn(): Flow<Boolean?>

    /** Persists the 18+ opt-in on the account (merge write; rules allowlist `adultContentOptIn`). */
    suspend fun setAdultOptIn(optedIn: Boolean): DataResult<Unit>
}
